// src/services/workspaceService.js
import { db, auth } from '../config/firebase';
import { 
  collection, doc, getDoc, getDocs, setDoc, addDoc, updateDoc, deleteDoc, 
  query, where, orderBy, serverTimestamp, writeBatch, arrayUnion, arrayRemove
} from 'firebase/firestore';
import { notificationService } from './notificationService';

/**
 * WorkspaceService
 * Manages workspaces within a diary book:
 * - Personal workspace (always present, cannot delete)
 * - Organisation workspaces (schools, businesses, churches, NPOs)
 * - Team/Group workspaces (standing teams, project committees, time-bound groups)
 * 
 * Collections:
 * - pb-books/{bookId}/workspaces
 * - pb-books/{bookId}/workspace-members
 * - pb-books/{bookId}/departments
 */

// Workspace types
export const WORKSPACE_TYPES = {
  PERSONAL: 'personal',
  ORGANISATION: 'organisation',
  TEAM: 'team'
};

// Organisation types
export const ORGANISATION_TYPES = {
  BUSINESS: { id: 'business', label: 'Business / Company', icon: 'Building2' },
  SCHOOL: { id: 'school', label: 'School / Educational Institution', icon: 'GraduationCap' },
  CHURCH: { id: 'church', label: 'Church / Religious Organisation', icon: 'Church' },
  NPO: { id: 'npo', label: 'Non-Profit Organisation', icon: 'Heart' },
  GOVERNMENT: { id: 'government', label: 'Government Unit', icon: 'Landmark' },
  COOPERATIVE: { id: 'cooperative', label: 'Cooperative / Union', icon: 'Users' },
  AGENCY: { id: 'agency', label: 'Agency / Consultancy', icon: 'Briefcase' },
  OTHER: { id: 'other', label: 'Other Organisation', icon: 'Building' }
};

// Team types
export const TEAM_TYPES = {
  STANDING: { id: 'standing', label: 'Standing Team', description: 'Permanent team (e.g., SMT, Finance)' },
  PROJECT: { id: 'project', label: 'Project Team', description: 'Time-bound project committee' },
  COMMITTEE: { id: 'committee', label: 'Committee', description: 'Governance or advisory committee' },
  DEPARTMENT: { id: 'department', label: 'Department Team', description: 'Departmental working group' },
  TASKTEAM: { id: 'taskteam', label: 'Task Team', description: 'Temporary task-focused group' }
};

// Member roles
export const MEMBER_ROLES = {
  OWNER: { id: 'owner', label: 'Owner', permissions: ['all'] },
  ADMIN: { id: 'admin', label: 'Administrator', permissions: ['manage', 'edit', 'view', 'share', 'invite'] },
  MANAGER: { id: 'manager', label: 'Manager', permissions: ['edit', 'view', 'share', 'assign'] },
  MEMBER: { id: 'member', label: 'Member', permissions: ['edit', 'view'] },
  VIEWER: { id: 'viewer', label: 'Viewer', permissions: ['view'] },
  GUEST: { id: 'guest', label: 'Guest', permissions: ['view'] }
};

export const workspaceService = {
  
  // ============================================================
  // WORKSPACES CRUD
  // ============================================================

  /**
   * Get all workspaces for a book (including personal)
   */
  async getWorkspaces(bookId) {
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) throw new Error('User must be authenticated');

      const workspacesRef = collection(db, 'pb-books', bookId, 'workspaces');
      const snapshot = await getDocs(query(workspacesRef, orderBy('createdAt', 'asc')));
      
      const workspaces = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      
      // Ensure personal workspace exists
      const personalExists = workspaces.some(w => w.type === WORKSPACE_TYPES.PERSONAL && w.ownerId === currentUser.uid);
      
      if (!personalExists) {
        const personalWorkspace = await this.ensurePersonalWorkspace(bookId, currentUser);
        workspaces.unshift(personalWorkspace);
      }

      // Filter: user's own workspaces or workspaces they're a member of
      return workspaces.filter(w => 
        w.ownerId === currentUser.uid || 
        w.memberIds?.includes(currentUser.uid) ||
        w.type === WORKSPACE_TYPES.PERSONAL
      );
    } catch (error) {
      console.error('Error getting workspaces:', error);
      return [];
    }
  },

  /**
   * Ensure personal workspace exists for a user
   */
  async ensurePersonalWorkspace(bookId, user) {
    try {
      const workspacesRef = collection(db, 'pb-books', bookId, 'workspaces');
      const q = query(workspacesRef, where('type', '==', WORKSPACE_TYPES.PERSONAL), where('ownerId', '==', user.uid));
      const snapshot = await getDocs(q);

      if (!snapshot.empty) {
        return { id: snapshot.docs[0].id, ...snapshot.docs[0].data() };
      }

      // Create personal workspace
      const personalData = {
        type: WORKSPACE_TYPES.PERSONAL,
        name: 'Personal',
        description: 'Your personal workspace',
        ownerId: user.uid,
        ownerEmail: user.email,
        memberIds: [user.uid],
        settings: {
          isDefault: true,
          canDelete: false
        },
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      const docRef = await addDoc(workspacesRef, personalData);
      return { id: docRef.id, ...personalData };
    } catch (error) {
      console.error('Error ensuring personal workspace:', error);
      throw error;
    }
  },

  /**
   * Get a single workspace
   */
  async getWorkspace(bookId, workspaceId) {
    try {
      const docRef = doc(db, 'pb-books', bookId, 'workspaces', workspaceId);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        return { id: docSnap.id, ...docSnap.data() };
      }
      return null;
    } catch (error) {
      console.error('Error getting workspace:', error);
      throw error;
    }
  },

  /**
   * Create an organisation workspace
   */
  async createOrganisation(bookId, orgData) {
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) throw new Error('User must be authenticated');

      const workspacesRef = collection(db, 'pb-books', bookId, 'workspaces');
      
      const organisationData = {
        type: WORKSPACE_TYPES.ORGANISATION,
        name: orgData.name,
        description: orgData.description || '',
        organisationType: orgData.organisationType || 'business',
        logoUrl: orgData.logoUrl || '',
        coverImageUrl: orgData.coverImageUrl || '',
        contactDetails: {
          email: orgData.email || '',
          phone: orgData.phone || '',
          address: orgData.address || '',
          website: orgData.website || ''
        },
        workingHours: {
          start: orgData.workingHoursStart || '09:00',
          end: orgData.workingHoursEnd || '17:00',
          days: orgData.workingDays || [1, 2, 3, 4, 5]
        },
        settings: {
          requireMeetingApproval: orgData.requireMeetingApproval || false,
          defaultMeetingDuration: orgData.defaultMeetingDuration || 60,
          allowGuestAccess: orgData.allowGuestAccess || false,
          enableDepartments: true,
          enableTeams: true
        },
        ownerId: currentUser.uid,
        ownerEmail: currentUser.email,
        memberIds: [currentUser.uid],
        adminIds: [currentUser.uid],
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      const docRef = await addDoc(workspacesRef, organisationData);
      
      // Add owner as member
      await this.addMember(bookId, docRef.id, {
        userId: currentUser.uid,
        email: currentUser.email,
        displayName: currentUser.displayName || currentUser.email,
        role: MEMBER_ROLES.OWNER.id
      });

      return { id: docRef.id, ...organisationData };
    } catch (error) {
      console.error('Error creating organisation:', error);
      throw error;
    }
  },

  /**
   * Create a team/group workspace
   */
  async createTeam(bookId, teamData) {
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) throw new Error('User must be authenticated');

      const workspacesRef = collection(db, 'pb-books', bookId, 'workspaces');
      
      const teamWorkspaceData = {
        type: WORKSPACE_TYPES.TEAM,
        name: teamData.name,
        description: teamData.description || '',
        purpose: teamData.purpose || '',
        teamType: teamData.teamType || TEAM_TYPES.STANDING.id,
        parentOrganisationId: teamData.parentOrganisationId || null,
        departmentId: teamData.departmentId || null,
        schedule: {
          startDate: teamData.startDate || null,
          endDate: teamData.endDate || null,
          autoDisband: teamData.autoDisband || false
        },
        settings: {
          isStandalone: !teamData.parentOrganisationId,
          visibility: teamData.visibility || 'members',
          allowMemberInvites: teamData.allowMemberInvites || false
        },
        linkedGoals: teamData.linkedGoals || [],
        linkedResources: teamData.linkedResources || [],
        ownerId: currentUser.uid,
        ownerEmail: currentUser.email,
        memberIds: [currentUser.uid],
        adminIds: [currentUser.uid],
        status: 'active',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      const docRef = await addDoc(workspacesRef, teamWorkspaceData);
      
      // Add owner as member
      await this.addMember(bookId, docRef.id, {
        userId: currentUser.uid,
        email: currentUser.email,
        displayName: currentUser.displayName || currentUser.email,
        role: MEMBER_ROLES.OWNER.id
      });

      return { id: docRef.id, ...teamWorkspaceData };
    } catch (error) {
      console.error('Error creating team:', error);
      throw error;
    }
  },

  /**
   * Update a workspace
   */
  async updateWorkspace(bookId, workspaceId, updates) {
    try {
      const docRef = doc(db, 'pb-books', bookId, 'workspaces', workspaceId);
      
      await updateDoc(docRef, {
        ...updates,
        updatedAt: serverTimestamp()
      });

      return true;
    } catch (error) {
      console.error('Error updating workspace:', error);
      throw error;
    }
  },

  /**
   * Delete a workspace (cannot delete personal workspace)
   */
  async deleteWorkspace(bookId, workspaceId) {
    try {
      const workspace = await this.getWorkspace(bookId, workspaceId);
      
      if (workspace?.type === WORKSPACE_TYPES.PERSONAL) {
        throw new Error('Cannot delete personal workspace');
      }

      const batch = writeBatch(db);
      
      // Delete workspace
      batch.delete(doc(db, 'pb-books', bookId, 'workspaces', workspaceId));
      
      // Delete members
      const membersRef = collection(db, 'pb-books', bookId, 'workspace-members');
      const membersQuery = query(membersRef, where('workspaceId', '==', workspaceId));
      const membersSnap = await getDocs(membersQuery);
      membersSnap.docs.forEach(d => batch.delete(d.ref));
      
      // Delete departments under this workspace
      const deptRef = collection(db, 'pb-books', bookId, 'departments');
      const deptQuery = query(deptRef, where('workspaceId', '==', workspaceId));
      const deptSnap = await getDocs(deptQuery);
      deptSnap.docs.forEach(d => batch.delete(d.ref));

      await batch.commit();
      return true;
    } catch (error) {
      console.error('Error deleting workspace:', error);
      throw error;
    }
  },

  // ============================================================
  // DEPARTMENTS / UNITS
  // ============================================================

  /**
   * Get departments for a workspace
   */
  async getDepartments(bookId, workspaceId) {
    try {
      const deptRef = collection(db, 'pb-books', bookId, 'departments');
      const q = query(deptRef, where('workspaceId', '==', workspaceId), orderBy('name', 'asc'));
      const snapshot = await getDocs(q);
      
      return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
      console.error('Error getting departments:', error);
      return [];
    }
  },

  /**
   * Create a department
   */
  async createDepartment(bookId, workspaceId, deptData) {
    try {
      const currentUser = auth.currentUser;
      const deptRef = collection(db, 'pb-books', bookId, 'departments');
      
      const departmentData = {
        workspaceId,
        name: deptData.name,
        description: deptData.description || '',
        headUserId: deptData.headUserId || null,
        parentDepartmentId: deptData.parentDepartmentId || null,
        createdBy: currentUser?.uid,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      const docRef = await addDoc(deptRef, departmentData);
      return { id: docRef.id, ...departmentData };
    } catch (error) {
      console.error('Error creating department:', error);
      throw error;
    }
  },

  /**
   * Update a department
   */
  async updateDepartment(bookId, departmentId, updates) {
    try {
      const docRef = doc(db, 'pb-books', bookId, 'departments', departmentId);
      await updateDoc(docRef, { ...updates, updatedAt: serverTimestamp() });
      return true;
    } catch (error) {
      console.error('Error updating department:', error);
      throw error;
    }
  },

  /**
   * Delete a department
   */
  async deleteDepartment(bookId, departmentId) {
    try {
      const docRef = doc(db, 'pb-books', bookId, 'departments', departmentId);
      await deleteDoc(docRef);
      return true;
    } catch (error) {
      console.error('Error deleting department:', error);
      throw error;
    }
  },

  // ============================================================
  // MEMBERS
  // ============================================================

  /**
   * Get members of a workspace
   */
  async getMembers(bookId, workspaceId) {
    try {
      const membersRef = collection(db, 'pb-books', bookId, 'workspace-members');
      const q = query(membersRef, where('workspaceId', '==', workspaceId));
      const snapshot = await getDocs(q);
      
      return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
      console.error('Error getting members:', error);
      return [];
    }
  },

  /**
   * Add a member to a workspace
   */
  async addMember(bookId, workspaceId, memberData, options = {}) {
    try {
      const membersRef = collection(db, 'pb-books', bookId, 'workspace-members');
      
      // Check if already a member
      const existingQuery = query(
        membersRef, 
        where('workspaceId', '==', workspaceId),
        where('userId', '==', memberData.userId)
      );
      const existing = await getDocs(existingQuery);
      
      if (!existing.empty) {
        const existingDoc = existing.docs[0];
        await updateDoc(existingDoc.ref, {
          ...memberData,
          updatedAt: serverTimestamp()
        });
        return { id: existingDoc.id, ...memberData };
      }

      const member = {
        workspaceId,
        userId: memberData.userId,
        email: memberData.email,
        displayName: memberData.displayName || memberData.email,
        photoUrl: memberData.photoUrl || '',
        role: memberData.role || MEMBER_ROLES.MEMBER.id,
        departmentId: memberData.departmentId || null,
        title: memberData.title || '',
        status: 'active',
        joinedAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      const docRef = await addDoc(membersRef, member);
      
      // Update workspace memberIds
      const workspaceRef = doc(db, 'pb-books', bookId, 'workspaces', workspaceId);
      await updateDoc(workspaceRef, {
        memberIds: arrayUnion(memberData.userId),
        updatedAt: serverTimestamp()
      });

      // Send notification for new members with email
      if (member.email && options.sendNotification !== false) {
        const workspace = await this.getWorkspace(bookId, workspaceId);
        if (workspace) {
          notificationService.sendMemberAddedNotification(
            bookId,
            member,
            workspace
          ).catch(err => console.warn('Member notification failed:', err));
        }
      }

      return { id: docRef.id, ...member };
    } catch (error) {
      console.error('Error adding member:', error);
      throw error;
    }
  },

  /**
   * Update a member's role or details
   */
  async updateMember(bookId, memberId, updates) {
    try {
      const docRef = doc(db, 'pb-books', bookId, 'workspace-members', memberId);
      await updateDoc(docRef, { ...updates, updatedAt: serverTimestamp() });
      return true;
    } catch (error) {
      console.error('Error updating member:', error);
      throw error;
    }
  },

  /**
   * Remove a member from a workspace
   */
  async removeMember(bookId, workspaceId, memberId, userId) {
    try {
      const batch = writeBatch(db);
      
      // Delete member document
      batch.delete(doc(db, 'pb-books', bookId, 'workspace-members', memberId));
      
      // Remove from workspace memberIds
      const workspaceRef = doc(db, 'pb-books', bookId, 'workspaces', workspaceId);
      batch.update(workspaceRef, {
        memberIds: arrayRemove(userId),
        updatedAt: serverTimestamp()
      });

      await batch.commit();
      return true;
    } catch (error) {
      console.error('Error removing member:', error);
      throw error;
    }
  },

  /**
   * Get user's workspaces across all types
   */
  async getUserWorkspaces(bookId, userId) {
    try {
      const workspacesRef = collection(db, 'pb-books', bookId, 'workspaces');
      const q = query(workspacesRef, where('memberIds', 'array-contains', userId));
      const snapshot = await getDocs(q);
      
      return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
      console.error('Error getting user workspaces:', error);
      return [];
    }
  },

  // ============================================================
  // CONTEXT HELPERS (for linking data to workspaces)
  // ============================================================

  /**
   * Get context object for new items
   */
  getItemContext(workspaceId, workspaceType, visibility = 'workspace') {
    const currentUser = auth.currentUser;
    return {
      ownerId: currentUser?.uid,
      workspaceId,
      contextType: workspaceType,
      visibility, // 'private', 'workspace', 'organisation', 'public'
      sharedWith: [],
      tags: [],
      createdBy: currentUser?.uid,
      createdAt: new Date().toISOString()
    };
  },

  /**
   * Check auto-disband for time-bound teams
   */
  async checkAutoDisbandTeams(bookId) {
    try {
      const workspacesRef = collection(db, 'pb-books', bookId, 'workspaces');
      const q = query(
        workspacesRef,
        where('type', '==', WORKSPACE_TYPES.TEAM),
        where('schedule.autoDisband', '==', true),
        where('status', '==', 'active')
      );
      const snapshot = await getDocs(q);
      
      const now = new Date();
      const batch = writeBatch(db);
      let disbandCount = 0;

      snapshot.docs.forEach(doc => {
        const team = doc.data();
        if (team.schedule?.endDate) {
          const endDate = new Date(team.schedule.endDate);
          if (endDate < now) {
            batch.update(doc.ref, {
              status: 'disbanded',
              disbandedAt: serverTimestamp()
            });
            disbandCount++;
          }
        }
      });

      if (disbandCount > 0) {
        await batch.commit();
      }

      return disbandCount;
    } catch (error) {
      console.error('Error checking auto-disband teams:', error);
      return 0;
    }
  },

  // ============================================================
  // TEAMS UNDER ORGANISATIONS
  // ============================================================

  /**
   * Get teams belonging to an organisation
   */
  async getOrganisationTeams(bookId, organisationId) {
    try {
      const workspacesRef = collection(db, 'pb-books', bookId, 'workspaces');
      const q = query(
        workspacesRef,
        where('type', '==', WORKSPACE_TYPES.TEAM),
        where('parentOrganisationId', '==', organisationId),
        where('status', '==', 'active')
      );
      const snapshot = await getDocs(q);
      
      return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
      console.error('Error getting organisation teams:', error);
      return [];
    }
  },

  /**
   * Get standalone teams (not part of any organisation)
   */
  async getStandaloneTeams(bookId, userId) {
    try {
      const workspacesRef = collection(db, 'pb-books', bookId, 'workspaces');
      const q = query(
        workspacesRef,
        where('type', '==', WORKSPACE_TYPES.TEAM),
        where('parentOrganisationId', '==', null),
        where('memberIds', 'array-contains', userId)
      );
      const snapshot = await getDocs(q);
      
      return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
      console.error('Error getting standalone teams:', error);
      return [];
    }
  }
};

export default workspaceService;